/**
 * Distance-based SFX manager — Web Audio.
 *
 * We can't head-track a phone in a pocket, so stereo panning would only
 * mislead. Each event therefore maps the listener→source distance to two cues:
 *   • volume attenuation (inverse-distance), and
 *   • a distance-scaled reverb (farther = wetter, longer tail) — which also
 *     "places" far events as out-there-and-echoey rather than up-close-and-dry.
 *
 * Web Audio has no EnvironmentalReverb, so reverb is approximated with a
 * ConvolverNode fed a synthesised exponential-decay impulse, whose wet/dry mix
 * and tail length track distance.
 *
 * Signal graph per voice:
 *   source → distGain ─┬─▶ dryGain ─▶ master
 *                      └─▶ wetGain ─▶ convolver ─▶ master
 */
import { audioContext, loadAudio, masterOut, sfxPath } from './context';

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Volume in [0,1]. A source *exactly* on the listener — distance 0, which only
 * your own "held" effects produce (shield/hulk/recharge are sent to you without
 * coordinates; see store.handleSfx) — plays at full volume. ANY distance away,
 * even a metre, drops immediately to half, then rolls off with the inverse-
 * distance law (amplitude ∝ 1/r, roughly how sound really fades). The deliberate
 * 1 → 0.5 step at zero makes it instantly clear whether a sound is coming from
 * *you* or from someone/somewhere else.
 */
const ROLLOFF_REF_M = 25; // reference distance for the 1/r tail
function distVol(distMeters: number): number {
  if (distMeters <= 0) return 1; // on top of you
  return clamp((0.5 * ROLLOFF_REF_M) / (ROLLOFF_REF_M + distMeters), 0, 1);
}

/**
 * Reverb parameters derived from distance. Tuned for this game's ~300 m play
 * radius: dry/tight up close, and markedly wetter with a longer tail toward the
 * edge of range.
 */
const REVERB_FULL_M = 200; // distance at which reverb reaches its wettest/longest
function reverbParams(distMeters: number): { tailS: number; wet: number } {
  const t = clamp(distMeters / REVERB_FULL_M, 0, 1);
  const tailS = 0.15 + 3.35 * t; // 0.15 s close → ~3.5 s at range
  const wet = clamp(0.9 * t, 0, 0.9); // dry close → ~0.9 wet at range
  return { tailS, wet };
}

/** Build an exponential-decay noise impulse response of the given length. */
function makeImpulse(tailS: number): AudioBuffer {
  const ctx = audioContext();
  const len = Math.max(1, Math.floor(ctx.sampleRate * tailS));
  const ir = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = ir.getChannelData(ch);
    for (let i = 0; i < len; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.5);
    }
  }
  return ir;
}

interface Voice {
  src: AudioBufferSourceNode;
  dist: GainNode;
  dry: GainNode;
  wet: GainNode;
  conv: ConvolverNode;
  /** current convolver tail length (s), to avoid rebuilding the IR needlessly */
  tailS?: number;
}

export class SfxManager {
  private master: GainNode;
  private loops = new Map<number, Voice>();
  /**
   * In-flight loop loads, keyed by sid. A loop's buffer is fetched/decoded
   * asynchronously, so a `stop` (or a newer `start`) can arrive before the
   * source exists. The token lets the load detect that it was superseded and
   * abort instead of starting an orphan that never receives its stop — the
   * "bomb keeps beeping after it should have stopped" bug.
   */
  private loads = new Map<number, symbol>();

  constructor() {
    const ctx = audioContext();
    this.master = ctx.createGain();
    this.master.gain.value = 1;
    this.master.connect(masterOut());
  }

  /** Fire a one-shot sound attenuated for `dist` metres away. */
  async playOneShot(name: string, dist: number): Promise<void> {
    const buf = await loadAudio(sfxPath(name));
    if (!buf) {
      console.warn('[sfx] missing:', name);
      return;
    }
    const v = this.makeVoice(buf, dist, false);
    v.src.onended = () => this.disposeVoice(v);
    v.src.start();
  }

  /**
   * Start (or replace) a tracked sound identified by `sid`. Looping by default;
   * pass `loop=false` for a finite positioned clip whose length already matches
   * its effect (e.g. the 20 s shield buzz). Either way the sound is distance-
   * tracked while it plays and disposed by a matching `stopLoop` — for a finite
   * clip that stop also cleans up the spent source after it ends on its own.
   */
  async startLoop(sid: number, name: string, dist: number, loop = true): Promise<void> {
    this.stopLoop(sid);
    const token = Symbol(name);
    this.loads.set(sid, token);
    const buf = await loadAudio(sfxPath(name));
    // A stop (or a newer start) raced in while we were fetching — our token is
    // gone or replaced. Abort so we never start a loop that already lost its stop.
    if (this.loads.get(sid) !== token) return;
    this.loads.delete(sid);
    if (!buf) {
      console.warn('[sfx] missing loop:', name);
      return;
    }
    const v = this.makeVoice(buf, dist, loop);
    this.loops.set(sid, v);
    v.src.start();
  }

  /** Re-attenuate an active loop as the listener moves (distance changed). */
  updateLoop(sid: number, dist: number): void {
    const v = this.loops.get(sid);
    if (!v) return;
    this.applyDistance(v, dist);
  }

  stopLoop(sid: number): void {
    // Cancel any in-flight load so a still-fetching loop never starts.
    const hadPendingLoad = this.loads.delete(sid);
    const v = this.loops.get(sid);
    if (!v) {
      // Nothing playing and nothing loading under this sid — a genuinely
      // unknown stop (sid mismatch / double stop). Logged so the "buzz won't
      // stop" case stays diagnosable; a cancelled pending load is expected.
      if (!hadPendingLoad) console.warn('[sfx] stopLoop: no active loop for sid', sid);
      return;
    }
    this.loops.delete(sid);
    try {
      v.src.stop();
    } catch {
      /* already stopped */
    }
    this.disposeVoice(v);
  }

  /** Re-attenuate every active loop (called on each GPS fix as distance changes). */
  reattenuateAll(distFor: (sid: number) => number | null): void {
    for (const [sid, v] of this.loops) {
      const d = distFor(sid);
      if (d != null) this.applyDistance(v, d);
    }
  }

  stopAll(): void {
    // Cancel pending loads first so none of them start after we've swept.
    this.loads.clear();
    for (const sid of [...this.loops.keys()]) this.stopLoop(sid);
  }

  // ── internals ──────────────────────────────────────────────────────────
  private makeVoice(buf: AudioBuffer, dist: number, loop: boolean): Voice {
    const ctx = audioContext();
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = loop;
    const distGain = ctx.createGain();
    const dry = ctx.createGain();
    const wet = ctx.createGain();
    const conv = ctx.createConvolver();

    src.connect(distGain);
    distGain.connect(dry);
    distGain.connect(wet);
    wet.connect(conv);
    dry.connect(this.master);
    conv.connect(this.master);

    const v: Voice = { src, dist: distGain, dry, wet, conv };
    this.applyDistance(v, dist);
    return v;
  }

  private applyDistance(v: Voice, dist: number): void {
    const now = audioContext().currentTime;
    v.dist.gain.setTargetAtTime(distVol(dist), now, 0.05);
    const { tailS, wet } = reverbParams(dist);
    v.dry.gain.setTargetAtTime(1 - wet, now, 0.05);
    v.wet.gain.setTargetAtTime(wet, now, 0.05);
    // Only rebuild the (relatively expensive) impulse when tail changes a lot.
    if (v.tailS === undefined || Math.abs(v.tailS - tailS) > 0.25) {
      v.conv.buffer = makeImpulse(tailS);
      v.tailS = tailS;
    }
  }

  private disposeVoice(v: Voice): void {
    try {
      v.src.disconnect();
      v.dist.disconnect();
      v.dry.disconnect();
      v.wet.disconnect();
      v.conv.disconnect();
    } catch {
      /* noop */
    }
  }
}

export const sfx = new SfxManager();
